import { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { useSnackbar } from "../../context/SnackbarContext";
import {
  compressImage,
  type CompressionPreset,
} from "../../utils/imageCompression";
import {
  uploadToStorage,
  generateStorageFilename,
} from "../../services/storageService";
import {
  computeBlurhashFromBlob,
  computeBlurhashFromUrl,
} from "../../utils/blurhash";

const PRESET_LABELS: Record<CompressionPreset, string> = {
  headshot: "Profile photo",
  hero: "Hero image",
  logo: "Logo",
};

interface ImageUploadProps {
  /** Current download URL, or null if no image. */
  value: string | null;
  /** Called with the new download URL after upload, or null after delete. */
  onChange: (url: string | null) => void;
  /** Compression preset — controls dimensions, format, and quality. */
  preset: CompressionPreset;
  /** Storage path prefix, e.g. `events/{eventId}/hero`. The component
   * appends a generated filename. */
  storagePathPrefix: string;
  /** Optional label override. Defaults to the preset label. */
  label?: string;
  disabled?: boolean;
  /** Allow removing the current image. Defaults to true. */
  allowRemove?: boolean;
  /**
   * Static default images the user can pick from. Each entry is a URL path
   * served from the admin app's public/ directory (e.g. "/event-hero-1-city.png").
   * Selecting one sets the value directly — no Storage upload needed.
   */
  defaultImages?: string[];
  /**
   * Optional callback fired with a BlurHash string after a successful
   * upload (or null after delete). Wire this from the hero upload callsite
   * so the encoded preview gets persisted alongside `heroImageURL` and
   * mobile can paint a fuzzy placeholder while the real image loads.
   *
   * Headshot / logo callsites omit this — they don't need placeholder
   * previews and skipping the encode keeps small uploads cheap.
   */
  onBlurhashChange?: (blurhash: string | null) => void;
}

/**
 * Reusable image upload field for admin forms. Handles file picker,
 * client-side compression, storage upload, and preview rendering.
 *
 * Used for direct-upload paths (event hero, sponsor logo). The
 * user-photo-on-behalf path uses the useSetUserPhoto hook instead, which
 * routes through the setUserPhotoOnBehalf callable.
 */
export function ImageUpload({
  value,
  onChange,
  preset,
  storagePathPrefix,
  label,
  disabled = false,
  allowRemove = true,
  defaultImages = [],
  onBlurhashChange,
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const { showError } = useSnackbar();

  const displayLabel = label ?? PRESET_LABELS[preset];

  function openPicker() {
    inputRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset the input so picking the same file twice still fires onChange
    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      showError("Please select an image file");
      return;
    }

    setUploading(true);
    try {
      const { blob, contentType } = await compressImage(file, preset);
      const extension = contentType.split("/")[1] ?? "jpg";
      const filename = generateStorageFilename(extension);
      const path = `${storagePathPrefix.replace(/\/$/, "")}/${filename}`;
      const url = await uploadToStorage(path, blob, contentType);
      onChange(url);
      // Encode blur preview from the same compressed blob we just
      // uploaded so we don't pay a second network round-trip. Failure
      // is non-fatal: the consumer falls back to a solid color
      // placeholder driven by heroOverlayColor.
      if (onBlurhashChange) {
        const hash = await computeBlurhashFromBlob(blob);
        onBlurhashChange(hash);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Upload failed";
      showError(`Image upload failed: ${message}`);
    } finally {
      setUploading(false);
    }
  }

  function handleRemove() {
    onChange(null);
    onBlurhashChange?.(null);
  }

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
        {displayLabel}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {/* Preview */}
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: 1,
            border: "1px dashed",
            borderColor: "divider",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            position: "relative",
            backgroundColor: "background.default",
          }}
        >
          {uploading ? (
            <CircularProgress size={32} />
          ) : value ? (
            <Box
              component="img"
              src={value}
              alt={displayLabel}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <ImageIcon size={32} color="currentColor" />
          )}
          {value && allowRemove && !uploading && !disabled && (
            <IconButton
              size="small"
              onClick={handleRemove}
              aria-label={`Remove ${displayLabel.toLowerCase()}`}
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                backgroundColor: "background.paper",
                "&:hover": { backgroundColor: "background.paper" },
              }}
            >
              <X size={14} />
            </IconButton>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Upload size={16} />}
            onClick={openPicker}
            disabled={disabled || uploading}
          >
            {value ? "Replace" : "Upload"}
          </Button>
          <Typography variant="caption" color="text.secondary">
            JPG, PNG, or WebP. Auto-compressed.
          </Typography>
        </Box>
      </Box>

      {defaultImages.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Or choose a default
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {defaultImages.map((src) => {
              const isSelected = value === src;
              return (
                <Box
                  key={src}
                  component="img"
                  src={src}
                  alt=""
                  onClick={() => {
                    if (disabled || uploading) return;
                    onChange(src);
                    // Compute blurhash for the default asset right now
                    // (we have the URL and the bytes are static), so the
                    // form has a non-null heroBlurhash to submit even
                    // though the actual Storage upload happens later in
                    // EventForm.uploadDefaultHeroIfNeeded.
                    if (onBlurhashChange) {
                      void computeBlurhashFromUrl(src).then(onBlurhashChange);
                    }
                  }}
                  sx={{
                    width: 80,
                    height: 48,
                    objectFit: "cover",
                    borderRadius: 1,
                    cursor: disabled || uploading ? "default" : "pointer",
                    border: 2,
                    borderColor: isSelected ? "primary.main" : "transparent",
                    opacity: disabled || uploading ? 0.5 : 1,
                    transition: "border-color 0.15s, opacity 0.15s",
                    "&:hover": disabled || uploading ? {} : { borderColor: "primary.light" },
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
    </Box>
  );
}
